import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  featureFlagService,
  type FeatureFlagKey,
} from '@/api/services/featureFlags';

export type { FeatureFlagKey } from '@/api/services/featureFlags';

export type FeatureFlagsMap = Record<FeatureFlagKey, boolean>;

const DEFAULT_FLAGS: FeatureFlagsMap = {
  marketplace: true,
  customer_billing: true,
  stripe: false,
  telegram: true,
  telethon: true,
  maintenance: true,
  experimental_scrapers: false,
  future_ai_templates: false,
};

type FeatureFlagContextValue = {
  flags: FeatureFlagsMap;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

const FeatureFlagContext = createContext<FeatureFlagContextValue | null>(null);

export function FeatureFlagProvider({ children }: { children: React.ReactNode }) {
  const [flags, setFlags] = useState<FeatureFlagsMap>(DEFAULT_FLAGS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    try {
      const response = await featureFlagService.effective();
      const next = { ...DEFAULT_FLAGS };
      for (const [key, value] of Object.entries(response.flags)) {
        if (key in next) {
          next[key as FeatureFlagKey] = Boolean(value.enabled);
        }
      }
      setFlags(next);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load feature flags');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const value = useMemo(
    () => ({ flags, loading, error, refresh }),
    [flags, loading, error],
  );

  return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
}

export function useFeatureFlags(): FeatureFlagContextValue {
  const ctx = useContext(FeatureFlagContext);
  if (!ctx) {
    throw new Error('useFeatureFlags must be used within FeatureFlagProvider');
  }
  return ctx;
}

export function useFeatureFlag(key: FeatureFlagKey): boolean {
  const { flags } = useFeatureFlags();
  return flags[key];
}
